"use client";

import { motion } from "motion/react";
import { type ComponentProps, forwardRef, useEffect } from "react";
import { match } from "ts-pattern";
import { cn } from "../../..";
import { getCSSVar } from "../../lib/utilities";

type VariantProps =
	| {
			variant: "controlled";
			open: boolean;
	  }
	| {
			variant: "animated" | "state";
			open?: never;
	  };

type DivProps = Pick<ComponentProps<"div">, "className" | "onClick" | "onMouseEnter">;

type OverlayProps = DivProps;

export const ScreenOverlay = forwardRef<HTMLDivElement, OverlayProps & VariantProps>(
	function ScreenOverlayInner(props, ref) {
		const { open, variant, className, ...remainingProps } = props;

		useEffect(() => {
			const isAlreadyOpen = getCSSVar("--dialog-open") === 1;
			document.documentElement.style.setProperty(
				"--dialog-open",
				isAlreadyOpen || open || variant !== "controlled" ? "1" : "0",
			);

			if (window.innerHeight) {
				document.documentElement.style.setProperty("--window-y", `${window.innerHeight}px`);
			}

			return () => {
				document.documentElement.style.removeProperty("--dialog-open");
				document.documentElement.style.removeProperty("--window-y");
			};
		}, [variant, open]);

		return match(variant)
			.with("animated", () => (
				<motion.div
					ref={ref}
					className={cn("fixed inset-0 z-dialog bg-shades-dark", className)}
					initial={{ opacity: 0 }}
					animate={{ opacity: 0.25 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.15 }}
					{...remainingProps}
				/>
			))
			.with("controlled", () => (
				<div
					ref={ref}
					className={cn("fixed inset-0 z-dialog motion-safe:transition-all", className, {
						"visible bg-shades-dark/25": open,
						"invisible bg-shades-dark/0": !open,
					})}
					{...remainingProps}
				/>
			))
			.with("state", () => (
				<div
					ref={ref}
					className={cn(
						"fixed inset-0 z-dialog rdx-state-closed:animate-fadeOut rdx-state-open:animate-fadeIn bg-shades-dark/25",
						className,
					)}
					{...remainingProps}
				/>
			))
			.otherwise(() => null);
	},
);
